import { getQueryParams } from '../../components/helpers';
import conf, { getBuydotEnv } from '../../conf';
import { setFeaturesLoaded } from '../../store/data';
import { toggleFeature } from '../../store/features';
import fetch from '../fetch';

import type { Dispatch } from '@avalara/types/actions';

// Query-param overrides that must never work in production, regardless of what the URL says --
// each name is the feature key as it appears in the query string, compared case-insensitively.
// shouldShowProductCatalog gates the portal-integration catalog work (still unreleased); a
// production visitor guessing this query param must not be able to turn it on for themselves.
// enableCrossSell (BRGUI-2988) is the master kill-switch for the catalog, every PDP and the checkout
// nav chrome; like shouldShowProductCatalog it gates whole-page render rather than data, so a stray
// prod override would only produce a confusing half-rendered state.
const PROD_DISALLOWED_QUERY_OVERRIDES = new Set(['shouldshowproductcatalog', 'enablecrosssell']);

export const configureFeatures = (search: string) => async (dispatch: Dispatch) => {
  try {
    const response = await fetch(dispatch, { showLoadingWheel: false })<Record<string, boolean>>(
      `${conf.API_URL}/api/features`,
    );

    if (response.ok) {
      for (const feature of Object.keys(response.value)) {
        dispatch(toggleFeature({ feature, showFeature: response.value[feature] }));
      }
    }
  } catch {
    // The fetch helper already converts network/transport failures into a Result with ok: false;
    // this only guards against something above it (e.g. getOwdBearerToken) rejecting outright, so
    // featuresLoaded below still fires instead of leaving every gated consumer stuck forever.
  }

  const queryParams = getQueryParams(search);
  const isProd = getBuydotEnv() === 'prod';

  for (const feature of Object.keys(queryParams)) {
    if (isProd && PROD_DISALLOWED_QUERY_OVERRIDES.has(feature.toLowerCase())) continue;

    const featureValue = queryParams[feature];
    if (typeof featureValue === 'string') {
      const showFeature = featureValue.toUpperCase() === 'TRUE';
      dispatch(toggleFeature({ feature, showFeature }));
    } else {
      dispatch(toggleFeature({ feature, showFeature: !!featureValue }));
    }
  }

  dispatch(setFeaturesLoaded());
};
